import React from 'react';

import { Issue } from '@qality/common-components/src/molecules/Issue';
import { TestTypePopupSelectProvider } from '@qality/common-components/src/select/TestTypePopupSelect';
import { FEATURE_FLAG } from '@qality/common-config';
import { RepositoryIssue, TestType } from '@qality/common-model';
import { useTestTypesContext } from '@qality/common-pages';
import {
  StorageOfUniqueServices,
  TestCasesService,
} from '@qality/common-service';
import { DEFAULT_TEST_TYPE } from '@qality/common-utils/src/TestType.const';

import { useTestCaseRepositoryIssueTypesContext } from '../TestCaseRepositoryIssueTypesProvider';
import { useTestCaseRepositoryIssuesContext } from '../TestCaseRepositoryIssuesProvider';

type Props = {
  issue: RepositoryIssue;
  issueLink?: string;
};

export default function TestCaseRepositoryIssue({
  issue,
  issueLink,
}: Props): JSX.Element {
  const { getTestType } = useTestTypesContext();
  const { issues } = useTestCaseRepositoryIssuesContext();
  const { testCasesTestTypes, updateTestCasesTestTypes } =
    useTestCaseRepositoryIssueTypesContext();

  const findTestType = (): TestType => {
    if (!FEATURE_FLAG.isPremium) {
      return DEFAULT_TEST_TYPE;
    }

    if (!(testCasesTestTypes && issues && issue.key)) {
      return null;
    }

    const foundTestCaseTestTypeId = testCasesTestTypes.find(
      (testCaseTestType) => testCaseTestType.testCaseId === +issue.id
    )?.testTypeId;

    if (!foundTestCaseTestTypeId) {
      return null;
    }

    const foundTestType = getTestType(foundTestCaseTestTypeId);

    if (!foundTestType) {
      return null;
    }

    return foundTestType;
  };

  const updateTestType = async (selectedTestType: TestType) => {
    const testTypeBackup = testType;

    try {
      updateTestCasesTestTypesContext(null);
      await TestCasesService.bulkUpdateTestCaseTestTypes([
        { id: +issue.id, testTypeId: selectedTestType.id },
      ]);
      updateTestCasesTestTypesContext(selectedTestType);
    } catch (error) {
      updateTestCasesTestTypesContext(testTypeBackup);
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        error,
        'Failed to update test case test type'
      );
    }
  };

  const updateTestCasesTestTypesContext = async (
    selectedTestType: TestType
  ) => {
    const updatedIssueTestTypes = testCasesTestTypes.map((issueTestType) => {
      if (issueTestType.testCaseId === +issue.id) {
        return { testCaseId: +issue.id, testTypeId: selectedTestType?.id };
      }
      return issueTestType;
    });
    updateTestCasesTestTypes(updatedIssueTestTypes);
  };

  const selectedTestTypeIsDifferentThanCurrent = (
    selectedTestType: TestType
  ) => {
    return testType?.id !== selectedTestType.id;
  };

  const testType = findTestType();

  return (
    <TestTypePopupSelectProvider
      selectedTestType={testType}
      setSelectedTestType={(selectedTestType: TestType) => {
        if (selectedTestTypeIsDifferentThanCurrent(selectedTestType)) {
          updateTestType(selectedTestType);
        }
      }}
    >
      <Issue
        issueKey={issue.key}
        issueLink={issueLink}
        withEllipsis
        testTypeProps={{
          testType: testType,
          isLoading: !testType,
          isReadOnly: false,
          style: { paddingRight: '6px', paddingTop: '6px' },
        }}
      />
    </TestTypePopupSelectProvider>
  );
}
